import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { Eyebrow, Reveal } from "@/components/public/Motion";

const KIND = { news: "News", event: "Event", talk: "Talk" };
const day = (d) => new Date(`${d}T12:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

export const PostCard = ({ p }) => (
  <Link to={`/news/${p.slug}`} data-testid={`post-card-${p.id}`} className="group block h-full overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] transition-colors hover:border-[#FEDC19]/60">
    <div className="relative h-48 overflow-hidden bg-[#1a1b20]">
      {p.image && <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />}
      <span className={`absolute left-4 top-4 rounded-full px-3 py-1 text-xs font-bold ${p.kind === "news" ? "bg-[#FEDC19] text-black" : "bg-[#DB0000] text-white"}`}>{KIND[p.kind]}</span>
    </div>
    <div className="p-5">
      <div className="flex flex-wrap items-center gap-3 text-xs text-white/60"><span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {day(p.date)}</span>{p.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {p.location}</span>}</div>
      <h3 className="mt-2 text-lg font-bold leading-snug">{p.title}</h3>
      <p className="mt-2 line-clamp-3 text-sm text-white/65">{p.summary}</p>
    </div>
  </Link>
);

const Header = ({ eyebrow, title, text, img }) => (
  <section className="relative overflow-hidden pb-16 pt-36">
    <img src={img} alt="" className="kenburns absolute inset-0 h-full w-full object-cover opacity-40" />
    <div className="absolute inset-0 bg-gradient-to-b from-[#0d0e11]/60 to-[#0d0e11]" />
    <div className="relative mx-auto max-w-7xl px-5"><Eyebrow>{eyebrow}</Eyebrow><h1 className="mt-3 max-w-3xl text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">{title}</h1>{text && <p className="mt-5 max-w-2xl text-white/75">{text}</p>}</div>
  </section>
);

export function PostList({ kinds }) {
  const [items, setItems] = useState(null);
  const events = kinds.includes("event");
  useEffect(() => {
    Promise.all(kinds.map((k) => api.get("/public/posts", { params: { kind: k, limit: 100 } }))).then((rs) => {
      const all = rs.flatMap((r) => r.data);
      if (!events) return setItems(all);
      const today = new Date().toISOString().slice(0, 10);
      const up = all.filter((p) => p.date >= today).sort((a, b) => a.date.localeCompare(b.date));
      const past = all.filter((p) => p.date < today).sort((a, b) => b.date.localeCompare(a.date));
      setItems([...up, ...past]);
    }).catch(() => setItems([]));
  }, [kinds.join()]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid={events ? "public-events" : "public-news"}>
      <Header eyebrow={events ? "Events & talks" : "Latest news"} img={events ? "/site/fundraising.jpg" : "/site/latest-news.jpg"}
        title={events ? "Meet the team, hear our stories" : "What our blood bikers have been up to"}
        text={events ? "Fundraising events across the three counties, plus free talks from our Speaker Team. To book a talk email talks@ssbbgroup.org.uk." : null} />
      <div className="mx-auto grid max-w-7xl gap-5 px-5 pb-24 md:grid-cols-2 lg:grid-cols-3">
        {items === null ? <div className="h-40 animate-pulse rounded-3xl bg-white/5" /> : items.length === 0 ? <p className="text-white/60" data-testid="posts-empty">Nothing here yet, check back soon.</p>
          : items.map((p, n) => <Reveal key={p.id} delay={(n % 3) * 80}><PostCard p={p} /></Reveal>)}
      </div>
    </div>
  );
}

export function PostDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(undefined);
  useEffect(() => { api.get(`/public/posts/${slug}`).then((r) => setP(r.data)).catch(() => setP(null)); }, [slug]);
  if (p === undefined) return <div className="h-screen" />;
  if (p === null) return <div className="mx-auto max-w-3xl px-5 pb-24 pt-36"><h1 className="text-3xl font-black">Post not found</h1><Link to="/news" className="mt-4 inline-block text-[#FEDC19]">Back to news</Link></div>;
  return (
    <article data-testid="post-detail">
      <Header eyebrow={`${KIND[p.kind]} · ${day(p.date)}${p.location ? ` · ${p.location}` : ""}`} title={p.title} text={p.summary} img={p.image || "/site/slider3-1.jpg"} />
      <div className="mx-auto max-w-3xl px-5 pb-24">
        {p.body && <div className="whitespace-pre-line text-lg leading-relaxed text-white/80">{p.body}</div>}
        {p.link && <a href={p.link} target="_blank" rel="noreferrer" className="mt-8 inline-block rounded-full bg-[#FEDC19] px-6 py-3 font-bold text-black">Find out more</a>}
        <Link to={p.kind === "news" ? "/news" : "/events"} className="mt-10 flex items-center gap-2 text-sm font-bold text-[#FEDC19]"><ArrowLeft className="h-4 w-4" /> Back</Link>
      </div>
    </article>
  );
}

export { Header as PageHeader };
